import React from 'react'

import classNames from 'clsx'
import memoizeOne from 'memoize-one'
import PropTypes from 'prop-types'
import { pick } from 'ramda'

import { Skeleton } from '@mui/material'
import { makeStyles } from '@mui/styles'

import createLogger from '~/src/Lib/Logging'
import {
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  rafThrottle,
  shallowEquals,
  useFilestackURL,
} from '~/src/Lib/Utils'

const displayName = 'LoadingAttachment'
const logger = createLogger(displayName)

const useStyles = makeStyles(theme => ({
  root: {
    display: 'inline-block',
    width: '100%',
    '& > div': {
      margin: '0 auto'
    }
  },
  placeholder: {
    display: 'flex',
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
    '& svg': {
      opacity: theme.palette.states.disabled,
    }
  },
}), { name: displayName })

const getDimensions = memoizeOne((parentWidth, naturalWidth = parentWidth, naturalHeight = parentWidth * (9 / 16)) => {
  const width = Math.min(parentWidth, naturalWidth)
  const height = width < naturalWidth ? naturalHeight * (width / naturalWidth) : naturalHeight * (naturalWidth / width)

  return { parentWidth, width, height }
}, (left, right) => shallowEquals(left, right, 2))

const placeholderSrc = (width, height) => `data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}"%3E%3C/svg%3E`

class LoadingAttachmentComponent extends React.Component {
  static displayName = displayName

  static propTypes = {
    alt: PropTypes.string,
    className: PropTypes.string,
    component: PropTypes.oneOfType([
      PropTypes.oneOf(['img', 'picture', 'video']),
      PropTypes.elementType
    ]),
    dimensions: PropTypes.shape({
      width: PropTypes.number,
      height: PropTypes.number,
    }),
    src: PropTypes.string.isRequired,
  }

  static defaultProps = {
    alt: 'image',
    className: '',
    component: 'img',
    dimensions: EMPTY_OBJECT,
  }

  state = {
    loading: true,
    width: undefined,
    height: undefined,
    parentWidth: null,
  }

  componentDidMount() {
    const { src } = this.props
    this.loaderImage = new Image()
    this.loaderImage.src = src
    this.loaderImage.onload = this.imageOnLoad
    this.getParentWidth()
  }

  componentDidUpdate() {
    this.getParentWidth()
  }

  setRef = ref => { this.child = ref }

  imageOnLoad = () => {
    if (!this.state.loading) return
    const { parentWidth } = this.state
    const { naturalHeight, naturalWidth } = this.loaderImage
    this.setState({
      loading: false,
      ...getDimensions(parentWidth, naturalWidth, naturalHeight)
    })
  }

  // Have to disable react/sort-comp because it thinks this is a regular instance prop,
  // not an auto-bound, wrapped method
  // eslint-disable-next-line react/sort-comp
  getParentWidth = rafThrottle(() => {
    const parent = this.child && this.child.closest('div')
    if (parent && parent.getBoundingClientRect) {
      const { width } = parent.getBoundingClientRect()
      if (width === this.state.parentWidth) return
      const { dimensions } = this.props
      const nextDimensions = getDimensions(width, dimensions.width, dimensions.height)
      if (shallowEquals(nextDimensions, pick(['parentWidth', 'width', 'height'], this.state))) {
        return
      }
      this.setState(nextDimensions)
    } else {
      requestAnimationFrame(this.getParentWidth)
    }
  })

  render() {
    const { classes, alt, src, className, component: Component } = this.props
    const { loading, width, height, parentWidth } = this.state

    const style = { width, height }

    const child = parentWidth && (
      <Component
        key={src}
        ref={this.setRef}
        src={loading ? placeholderSrc(width, height) : src}
        alt={alt}
        className={classNames(classes.image, className)}
        style={style}
      />
    )

    return (
      <div className={classNames(classes.root)}>
        {!parentWidth ? <span ref={this.setRef} /> : null}
        {parentWidth && loading ? (
          <Skeleton
            {...style}
            animation="wave"
            component="div"
            variant="rectangular"
          >
            {child}
          </Skeleton>
        ) : null}
        {parentWidth && !loading ? child : null}
      </div>
    )
  }
}

/**
 * Shows a loading state placeholder when an attachment image is loading
 * @component
 * @alias LoadingAttachment
 */
const Wrapper = props => {
  const { actions, attachment, ...rest } = props
  const classes = useStyles(props)
  const getFileStackUrl = useFilestackURL()
  const src = attachment?.url && attachment?.handle
    ? getFileStackUrl(attachment, [...actions, 'auto_image'])
    : URL.createObjectURL(attachment)

  return (
    <LoadingAttachmentComponent
      {...rest}
      classes={classes}
      dimensions={attachment?.dimensions}
      src={src}
    />
  )
}

Wrapper.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.string),
  attachment: PropTypes.shape({
    handle: PropTypes.string.isRequired,
    mimetype: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    dimensions: PropTypes.shape({ width: PropTypes.number, height: PropTypes.number }),
  }).isRequired,
}
Wrapper.defaultProps = {
  actions: EMPTY_ARRAY,
}

export default React.memo(Wrapper)
